import { Link, useNavigate } from 'react-router-dom';

import type { AppItem, Server } from '../types';
import './AppsPageList.css';

type AppsPageListProps = {
  apps: AppItem[];
  servers: Server[];
  loading: boolean;
  loadFailed: boolean;
  onRefresh: () => Promise<void>;
};

export default function AppsPageList({ apps, servers, loading, loadFailed, onRefresh }: AppsPageListProps) {
  const navigate = useNavigate();
  const status = loading
    ? 'Loading apps…'
    : loadFailed
      ? 'Could not load apps. Use Refresh to try again.'
      : apps.length === 0
        ? 'No apps yet.'
        : `${apps.length} ${apps.length === 1 ? 'app' : 'apps'} loaded.`;

  return (
    <div className="card full apps-page-list">
      <div className="card-header">
        <h2 id="apps-page-list-heading">Apps</h2>
        <button type="button" onClick={onRefresh} disabled={loading} aria-label="Refresh apps">
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <p className="status" role="status" aria-live="polite" aria-atomic="true">{status}</p>
      {apps.length > 0 && (
        <>
          <p className="status apps-page-list__hint" id="apps-page-list-hint">
            Scroll horizontally to see all columns.
          </p>
          <div
            className="apps-page-list__scroll"
            role="region"
            aria-labelledby="apps-page-list-heading"
            aria-describedby="apps-page-list-hint"
            aria-busy={loading}
            tabIndex={0}
          >
            <table aria-labelledby="apps-page-list-heading">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">GitLab</th>
                  <th scope="col">Production server</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((app) => (
                  <tr key={app.id} onClick={() => navigate(`/apps/${app.id}`)} className="table-row-link">
                    <td>
                      <Link
                        className="apps-page-list__app-link"
                        to={`/apps/${app.id}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {app.name}
                      </Link>
                    </td>
                    <td className="mono">{app.gitlab_project_path ?? app.gitlab_url ?? '-'}</td>
                    <td>{servers.find((s) => s.id === app.production_server_id)?.name ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
